import { getCurriculum } from '../../content/curriculum';

export const getLevelCount = () => getCurriculum().levels.length;

export const getLevelId = levelIndex => getCurriculum().levels[levelIndex].id;

export const getOnboardingContainerTexts = () => getCurriculum().assets.onboardingTexts;

export const getOnboardingContainerText = phaseIndex => getOnboardingContainerTexts()[phaseIndex];

// Get the title of level's movie
export const getLevelTitle = levelIndex => getCurriculum().levels[levelIndex].title;

// Get all the frames in a level
export const getFrames = levelIndex => getCurriculum().levels[levelIndex].frames;

// get number of frames in current level
export const getFrameCountInLevel = levelIndex => getFrames(levelIndex) && getFrames(levelIndex).length;

// get a specific frame from a level
export const getFrame = (levelIndex, frameIndex) => getFrames(levelIndex)[frameIndex];

// get the uuid of the frame, used for user selection events
export const getFrameUuid = state => getFrame(state.levelIndex, state.frameIndex).uuid;

// get the passage text of a specific frame in a level
export const getFramePassageText = state => getFrame(state.levelIndex, state.frameIndex).text;

// get the background image of a specific frame in a level
export const getFrameBackgroundImage = state => getFrame(state.levelIndex, state.frameIndex).backgroundImage;

// get the reel thumbnail of a specific frame in a level
export const getFrameReelThumbnail = state => getFrame(state.levelIndex, state.frameIndex).reelImage;

// get all tabs data for a specific frame in a level
export const getFrameTabs = (levelIndex, frameIndex) => getFrame(levelIndex, frameIndex).tabs;

export const getFrameScoring = (levelIndex, frameIndex) => getFrame(levelIndex, frameIndex).scoring;

// get all characters tab data for a specific frame in a level
export const getFrameTabCharacters = (levelIndex, frameIndex) => getFrameTabs(levelIndex, frameIndex).characters;

// get an array of character asset info for a single character in a frame in a level
// to populate the characters tab
export const getFrameTabCharacterOptions = (levelIndex, frameIndex, characterId) =>
  getFrameTabCharacters(levelIndex, frameIndex)[characterId].map(optionId =>
    (
      {
        id: optionId,
        ...getCharacterAssetInfo(characterId).options[optionId],
      }
    ),
  );

export const getCharacterDisplayName = state =>
  getCharacterAssetInfo(state.currentCharacterTabId).name;

// get list of sceneProp ids for sceneProp tab for a specific frame in a level
export const getFrameTabScenePropIds = (levelIndex, frameIndex) => getFrameTabs(levelIndex, frameIndex).sceneProps;

// get an array of sceneProp asset info to populate sceneProps tab for a specific frame in a level
export const getFrameTabSceneProps = (levelIndex, frameIndex) =>
  getFrameTabScenePropIds(levelIndex, frameIndex).map(scenePropId =>
    (
      {
        id: scenePropId,
        ...getScenePropAssetInfo(scenePropId),
      }
    ),
  );

// get a list of motivation ids for motivations tab for a specific frame in a level
export const getFrameTabMotivationIds = (levelIndex, frameIndex) => getFrameTabs(levelIndex, frameIndex).motivations;

// get an array of all motivations for a tab in a specific frame in a level
export const getFrameTabMotivations = (levelIndex, frameIndex) =>
  getFrameTabMotivationIds(levelIndex, frameIndex).map(motivationId =>
    (
      {
        id: motivationId,
        ...getMotivationAssetInfo(motivationId),
      }
    ),
  );

export const getNextStandardItemCharacterTabId = (levelIndex, frameIndex) => {
  const frameCount = getFrames(levelIndex).length;
  const nextTabCharacters = getFrameTabCharacters(levelIndex, Math.min(frameIndex + 1, frameCount - 1));
  return nextTabCharacters ? `${Object.keys(nextTabCharacters)[0]}` : '';
};

export const getThoughtsStartAlertFrameIndexes = (levelIndex) => {
  const frames = getFrames(levelIndex);
  return frames.map(({ scoring }) => Object.keys(scoring.motivations).length);
};

// get all asset info for a certain character
export const getCharacterAssetInfo = characterId => getCurriculum().assets.characters[characterId];

// get all asset info for a specific sceneProp
export const getScenePropAssetInfo = scenePropId => getCurriculum().assets.sceneProps[scenePropId];

// get all info for a specific motivation
export const getMotivationAssetInfo = motivationId => getCurriculum().assets.motivations[motivationId] || {};

// get character correctness information for a specific frame
export const getFrameCorrectCharacters = (levelIndex, frameIndex) =>
  getFrameScoring(levelIndex, frameIndex).characters;

// get required sceneProp information for a specific frame
export const getFrameRequiredSceneProps = (levelIndex, frameIndex) =>
  getFrameScoring(levelIndex, frameIndex).sceneProps.required;

// get optional sceneProp information for a specific frame
export const getFrameOptionalSceneProps = (levelIndex, frameIndex) =>
  getFrameScoring(levelIndex, frameIndex).sceneProps.optional;

// get correct motivations for a specific frame
export const getFrameCorrectMotivations = (levelIndex, frameIndex) =>
  getFrameScoring(levelIndex, frameIndex).motivations;

// get correct motivation for a specific character for a specific frame
export const getFrameCorrectMotivationForCharacter = (levelIndex, frameIndex, characterId) =>
  getFrameCorrectMotivations(levelIndex, frameIndex)[characterId];

export const getLevelReelImages = levelIndex => getFrames(levelIndex).map(level => level.reelImage);

// given a level and frame, generate an SVG map for level loading
export const getSvgsForFrame = (level, frame, isLevelStart = false) => {
  const { backgroundImage, reelImage, tabs } = getFrame(level, frame);
  const { characters, sceneProps } = tabs;
  const characterAssets = getCurriculum().assets.characters;
  const propAssets = getCurriculum().assets.sceneProps;
  const characterNames = Object.keys(characters);

  const svgMap = {};
  if (frame !== 0 && isLevelStart) {
    for (let i = 0; i <= frame; i++) {
      const reelTemp = getFrame(level, i).reelImage;
      svgMap[reelTemp] = reelTemp;
    }
  } else {
    svgMap[reelImage] = reelImage;
  }
  svgMap[backgroundImage] = backgroundImage;
  let currentCharacter = '';
  let thumbnail = '';
  let fullImage = '';
  for (let i = 0; i < characterNames.length; i++) {
    currentCharacter = characterNames[i];
    for (let j = 0; j < characters[currentCharacter].length; j++) {
      try {
        thumbnail = characterAssets[currentCharacter].options[characters[currentCharacter][j]].thumbnail;
        fullImage = characterAssets[currentCharacter].options[characters[currentCharacter][j]].fullImage;
      } catch (err) {
        console.warn(`key mismatch for character ${characters[currentCharacter][j]} valid poses for ` +
        `${characterAssets[currentCharacter].name} are ${Object.keys(characterAssets[currentCharacter].options)}`);
      }
      svgMap[thumbnail] = thumbnail;
      svgMap[fullImage] = fullImage;
    }
  }
  for (let i = 0; i < sceneProps.length; i++) {
    try {
      thumbnail = propAssets[sceneProps[i]].thumbnail;
      fullImage = propAssets[sceneProps[i]].fullImage;
    } catch (err) {
      console.warn(`key mismatch for prop level ${level} frame ${frame}: 
        ${propAssets[sceneProps[i]]}, ${sceneProps[i]}, ${i}`);
    }
    if (thumbnail !== undefined && fullImage !== undefined) {
      svgMap[thumbnail] = thumbnail;
      svgMap[fullImage] = fullImage;
    }
  }
  return svgMap;
};
